import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Loader, AlertCircle, ArrowRight } from 'lucide-react';
import { callCreateCheckout } from '../../services/functions';

type Props = {
  productId: 'vetaxis'|'staffaxis'|'noteaxis'|'suite';
  planId: string;
  quantity?: number;
  label?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
};

const PayFastButton: React.FC<Props> = ({
  productId,
  planId,
  quantity,
  label,
  variant = 'primary',
  size = 'md'
}) => {
  const [state, setState] = React.useState<{
    actionUrl?: string;
    fields?: Record<string,string>;
    error?: string;
    loading?: boolean;
  }>();

  const submitRef = React.useRef<HTMLFormElement>(null);

  const onCheckout = async () => {
    setState({ loading: true });
    try {
      const res = await callCreateCheckout({ productId, planId, quantity });
      setState({ ...res, loading: false });
      setTimeout(() => submitRef.current?.submit(), 50);
    } catch (err: any) {
      setState({
        error: err?.message || 'Could not initiate checkout',
        loading: false
      });
    }
  };

  const fields = useMemo(() => state?.fields || {}, [state]);

  // Get button text based on plan and product
  const getButtonText = () => {
    if (state?.loading) return 'Processing...';
    if (label) return label;
    if (planId === 'free') return 'Get Started';
    return 'Subscribe Now';
  };

  // Get button styles based on variant
  const getButtonStyles = () => {
    const baseStyles = "w-full font-semibold transition-all duration-200 flex items-center justify-center gap-2 relative overflow-hidden disabled:cursor-not-allowed";

    const sizeStyles = {
      sm: "px-4 py-2 text-sm rounded-lg",
      md: "px-6 py-3 text-base rounded-xl",
      lg: "px-8 py-4 text-lg rounded-xl"
    };

    const variantStyles = {
      primary: `
        bg-primary-500 hover:bg-primary-600 text-white shadow-lg hover:shadow-xl
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        disabled:opacity-50 disabled:hover:bg-primary-500
      `,
      secondary: `
        bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm
        border border-slate-300 dark:border-gray-600
        text-slate-700 dark:text-gray-300
        hover:bg-white/70 dark:hover:bg-gray-800/70
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
        disabled:opacity-50
      `,
      outline: `
        border-2 border-primary-500 text-primary-600 dark:text-primary-400
        hover:bg-primary-500 hover:text-white
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-primary-600
      `
    };

    return `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]}`;
  };

  // Get icon based on plan type
  const getIcon = () => {
    if (state?.loading) {
      return <Loader className="w-4 h-4 animate-spin" />;
    }
    if (planId === 'free') {
      return <ArrowRight className="w-4 h-4" />;
    }
    return <CreditCard className="w-4 h-4" />;
  };

  return (
    <div className="space-y-3">
      <motion.button
        onClick={onCheckout}
        disabled={state?.loading}
        whileHover={{ scale: state?.loading ? 1 : 1.02 }}
        whileTap={{ scale: state?.loading ? 1 : 0.98 }}
        className={getButtonStyles()}
      >
        {/* Loading overlay */}
        {state?.loading && (
          <div className="absolute inset-0 bg-current opacity-10 animate-pulse" />
        )}

        {/* Button content */}
        <span className="flex items-center gap-2">
          {getIcon()}
          {getButtonText()}
        </span>

        {/* Success ripple effect */}
        {state?.actionUrl && !state?.loading && (
          <motion.div
            className="absolute inset-0 bg-green-400 rounded-inherit"
            initial={{ scale: 0, opacity: 0.8 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 0.6 }}
          />
        )}
      </motion.button>

      {/* Error message */}
      {state?.error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center gap-2"
        >
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{state.error}</span>
        </motion.div>
      )}

      {/* Success message */}
      {state?.actionUrl && !state?.error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 flex items-center gap-2"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 0.5 }}
            className="w-4 h-4 text-green-500 flex-shrink-0"
          >
            ✓
          </motion.div>
          <span className="text-sm text-green-700 dark:text-green-300">
            Redirecting to checkout...
          </span>
        </motion.div>
      )}

      {/* Hidden form for PayFast submission */}
      {state?.actionUrl && (
        <form ref={submitRef} action={state.actionUrl} method="POST" className="hidden">
          {Object.entries(fields).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v} />
          ))}
        </form>
      )}
    </div>
  );
};

export default PayFastButton;

